import { useState, useEffect, useCallback, useRef } from "react";
import { router } from "../../../data/Services/ordersRouter.jsx";
import { useOrderStore } from "../../../store/orderStore.jsx";
import {
  convertRouterOrderToOrderObject,
  getOrderTypeDisplay,
  getOrderStatusDisplay,
} from "../../../utils/orderConversions.js";

// Hook to manage open orders subscription + formatting + order object creation
// Returns { openOrders } ; options { debug }
export const useOpenOrdersData = (options = {}) => {
  const { debug = false } = options;
  const [openOrders, setOpenOrders] = useState([]);
  // Track last processed orders to avoid unnecessary state updates
  const lastOrdersRef = useRef(null);

  // Get tombstone check function from order store (prevents ghost order resurrection)
  const isOrderTombstoned = useOrderStore((state) => state.isOrderTombstoned);

  const processOpenOrders = useCallback(
    (accountData) => {
      if (!accountData || !accountData.openOrders) {
        return [];
      }

      const ordersArray = [];

      Object.keys(accountData.openOrders).forEach((symbol) => {
        const symbolOrders = accountData.openOrders[symbol];

        if (Array.isArray(symbolOrders)) {
          symbolOrders.forEach((routerOrder) => {
            // Skip tombstoned orders (prevents ghost order resurrection from stale accountData)
            if (isOrderTombstoned(routerOrder.id)) {
              if (import.meta.env.DEV) {
                console.log(
                  `👻 [useOpenOrdersData] Blocked ghost order: ${routerOrder.id}`,
                );
              }
              return;
            }

            // Convert router order to standardized order object for display
            // Note: Do NOT add to order store here - that's handled by orderStore.initialize()
            const orderObject = convertRouterOrderToOrderObject(
              routerOrder,
              symbol,
            );

            // Create display data for the panel only
            ordersArray.push({
              id: orderObject.id,
              symbol: symbol,
              tipo: getOrderTypeDisplay(orderObject),
              qtd: orderObject.quantity,
              price:
                routerOrder.price || routerOrder.limit || routerOrder.stop || 0,
              status: getOrderStatusDisplay(orderObject),
              orderType: orderObject.orderType,
              originalOrder: orderObject,
              oco: routerOrder.oco,
              limit: routerOrder.limit,
              stop: routerOrder.stop,
              action: routerOrder.action,
            });
          });
        }
      });

      if (debug)
        console.log(
          "[useOpenOrdersData] processed",
          ordersArray.length,
          "orders",
        );
      return ordersArray;
    },
    [debug, isOrderTombstoned],
  );

  useEffect(() => {
    // Throttle report processing to avoid overwhelming the UI
    let reportThrottleTimer = null;
    let pendingReportUpdate = false;

    const handleAccountDataUpdate = (accountData) => {
      const newOrders = processOpenOrders(accountData);

      // Compare with last orders to avoid unnecessary state updates
      const lastOrders = lastOrdersRef.current;
      if (lastOrders && lastOrders.length === newOrders.length) {
        let hasChanges = false;
        for (let i = 0; i < newOrders.length; i++) {
          const curr = lastOrders[i];
          const next = newOrders[i];
          if (
            curr.id !== next.id ||
            curr.price !== next.price ||
            curr.status !== next.status
          ) {
            hasChanges = true;
            break;
          }
        }
        if (!hasChanges) return; // Skip update if no changes
      }

      lastOrdersRef.current = newOrders;
      setOpenOrders(newOrders);
    };

    const handleReportThrottled = (report) => {
      // Check if this is a final state (cancelled, filled, rejected)
      // For final states, request fresh accountData immediately to ensure cross-machine sync
      const isFinalState =
        report &&
        (report.state === "cancelled" ||
          report.state === "4" ||
          report.state === "filled" ||
          report.state === "2" ||
          report.state === "rejected" ||
          report.state === "8");

      if (isFinalState && router.state === "online") {
        // For final states, bypass throttling and request fresh data immediately
        // This ensures cross-machine order deletions are reflected promptly
        if (debug)
          console.log(
            "[useOpenOrdersData] Final state report, requesting fresh accountData",
          );
        router.getAccountDataAsync();
        return;
      }

      // Mark that we have a pending update
      pendingReportUpdate = true;

      // If timer is already running, let it handle the update
      if (reportThrottleTimer) return;

      // Process immediately for first report, then throttle
      reportThrottleTimer = setTimeout(() => {
        if (pendingReportUpdate && router.accountData) {
          // Use the same comparison logic as handleAccountDataUpdate
          const newOrders = processOpenOrders(router.accountData);
          const lastOrders = lastOrdersRef.current;

          let hasChanges =
            !lastOrders || lastOrders.length !== newOrders.length;
          if (!hasChanges && lastOrders) {
            for (let i = 0; i < newOrders.length; i++) {
              if (
                lastOrders[i].id !== newOrders[i].id ||
                lastOrders[i].price !== newOrders[i].price ||
                lastOrders[i].status !== newOrders[i].status
              ) {
                hasChanges = true;
                break;
              }
            }
          }

          if (hasChanges) {
            lastOrdersRef.current = newOrders;
            setOpenOrders(newOrders);
          }
          pendingReportUpdate = false;
        }
        reportThrottleTimer = null;
      }, 500); // Throttle to max 2 updates per second
    };

    const accountDataReceiver = { receiveAccountData: handleAccountDataUpdate };
    const reportReceiver = { receiveReport: handleReportThrottled };

    router.registerReceiver(accountDataReceiver);
    router.registerReceiver(reportReceiver);

    // Use cached account data if available (avoid redundant server request)
    // orderStore.initialize() handles the central getAccountDataAsync call
    if (router.accountData) {
      handleAccountDataUpdate(router.accountData);
    }

    return () => {
      if (reportThrottleTimer) clearTimeout(reportThrottleTimer);
      router.unregisterReceiver(accountDataReceiver);
      router.unregisterReceiver(reportReceiver);
    };
  }, [processOpenOrders]);

  return { openOrders };
};
